import {
  DialogFooter,
  DialogHeader,
  DialogSubtitle,
  DialogTitle,
  PrimaryButton,
} from '@src/components/common/dialogStyles';
import Input from '@src/components/common/Input';
import ItemDropdownSelect from '@src/components/common/ItemDropdownSelect';
import ToggleGroup from '@src/components/common/ToggleGroup';
import { ORIGINAL_VIDEO_LANGUAGE_OPTIONS } from '@src/config/languages';
import InfoIcon from '@src/svgs/InfoIcon';
import React, { useId } from 'react';
import styled from 'styled-components';

import {
  AFTER_SAVE_NOTE,
  AUDIO_LABEL,
  AUDIO_OPTIONS,
  AUDIO_TOO_LARGE,
  DEFAULT_STORY_TYPE,
  DETAILS_STEP_TITLE,
  FOLDER_LABEL,
  LANGUAGE_LABEL,
  stepSubtitle,
  STORY_NAME_CLEARED_HELPER,
  STORY_NAME_HELPER,
  STORY_NAME_LABEL,
  STORY_NAME_PLACEHOLDER,
  STORY_TYPE_LABEL,
  STORY_TYPE_OPTIONS,
} from './uploadCopy';
import { DialogNote as Note } from './uploadDialogStyles';

import type { StoryType } from './uploadCopy';
import type { StoryVideoMusicStrategy } from './useStoryCreator';

type Props = {
  storyType: StoryType | null;
  setStoryType: (t: StoryType) => void;
  videoMusicStrategy: StoryVideoMusicStrategy;
  setVideoMusicStrategy: (s: StoryVideoMusicStrategy) => void;
  audioEnhancementDisabled?: boolean;
  storyName: string;
  setStoryName: (s: string) => void;
  /** True when the pre-filled file name looked like an id and was cleared. */
  nameWasCleared?: boolean;
  maxStoryTitleLength: number;
  storyLanguage: string | undefined;
  setStoryLanguage: (s: string) => void;
  allowLangSelect?: boolean;
  showFolderPicker?: boolean;
  folderOptions?: { caption: React.ReactNode; value: string | null }[];
  selectedFolderId?: string | null;
  setSelectedFolderId?: (id: string | null) => void;
  canSave: boolean;
  onSave: () => void;
};

/**
 * Step 2 of the Add Stories dialog (PLAT-11942): the old "Story Details"
 * screens one and two in one card. Both choices are pre-answered with the
 * common case and explained in one line, so most people only press Save.
 * The upload started when the file was picked and runs on in the
 * background; its progress lives on the Story Manager row and toast.
 */
export function StoryDetailsStep({
  storyType,
  setStoryType,
  videoMusicStrategy,
  setVideoMusicStrategy,
  audioEnhancementDisabled = false,
  storyName,
  setStoryName,
  nameWasCleared = false,
  maxStoryTitleLength,
  storyLanguage,
  setStoryLanguage,
  allowLangSelect = false,
  showFolderPicker = false,
  folderOptions = [],
  selectedFolderId = null,
  setSelectedFolderId,
  canSave,
  onSave,
}: Props) {
  const nameId = useId();
  const tooLong = storyName.length > maxStoryTitleLength;
  const activeType = storyType ?? DEFAULT_STORY_TYPE;

  return (
    <Form
      onSubmit={e => {
        e.preventDefault();
        if (canSave) onSave();
      }}
    >
      <DialogHeader>
        <DialogTitle>{DETAILS_STEP_TITLE}</DialogTitle>
        <DialogSubtitle>{stepSubtitle(2)}</DialogSubtitle>
      </DialogHeader>

      {/* Two short questions, each answered with one click and explained in
          one line that follows the current answer (docs/design-system.md →
          Explained choices). Both are pre-answered with the common case. */}
      <TwoColumns>
        <Field>
          <FieldLabel as="span">{STORY_TYPE_LABEL}</FieldLabel>
          <ToggleGroup
            tone="accent"
            ariaLabel={STORY_TYPE_LABEL}
            testIdPrefix="story-type"
            value={storyType ?? DEFAULT_STORY_TYPE}
            onChange={setStoryType}
            options={STORY_TYPE_OPTIONS.map(o => ({
              id: o.value,
              label: o.label,
            }))}
          />
          <ChoiceExplanation data-testid="story-type-explanation">
            {
              STORY_TYPE_OPTIONS.find(
                o => o.value === (storyType ?? DEFAULT_STORY_TYPE),
              )?.description
            }
          </ChoiceExplanation>
        </Field>

        <Field>
          <FieldLabel as="span">{AUDIO_LABEL[activeType]}</FieldLabel>
          {/* Over 8 GB only "keep" is possible: the whole group is disabled
              rather than one segment (Toggle Group rule), so the control and
              the step keep their size. */}
          <ToggleGroup
            tone="accent"
            ariaLabel={AUDIO_LABEL[activeType]}
            testIdPrefix="audio"
            disabled={audioEnhancementDisabled}
            value={audioEnhancementDisabled ? 'keep' : videoMusicStrategy}
            onChange={setVideoMusicStrategy}
            options={AUDIO_OPTIONS[activeType].map(o => ({
              id: o.value,
              label: o.label,
            }))}
          />
          <ChoiceExplanation data-testid="audio-explanation">
            {audioEnhancementDisabled
              ? AUDIO_TOO_LARGE[activeType]
              : AUDIO_OPTIONS[activeType].find(
                  o => o.value === videoMusicStrategy,
                )?.description}
          </ChoiceExplanation>
        </Field>
      </TwoColumns>

      <Field>
        <Input
          id={nameId}
          fullWidth
          required
          label={STORY_NAME_LABEL}
          placeholder={STORY_NAME_PLACEHOLDER}
          value={storyName}
          maxLength={maxStoryTitleLength}
          onKeyDown={e => e.stopPropagation()}
          onChange={e => setStoryName(e.target.value)}
          data-testid="story-name-input"
        />
        <HelperRow>
          <Helper>
            {nameWasCleared && !storyName
              ? STORY_NAME_CLEARED_HELPER
              : STORY_NAME_HELPER}
          </Helper>
          <Helper $error={tooLong}>
            {storyName.length}/{maxStoryTitleLength}
          </Helper>
        </HelperRow>
      </Field>

      {(allowLangSelect || showFolderPicker) && (
        <TwoColumns>
          {allowLangSelect && (
            <Field>
              <FieldLabel as="span">{LANGUAGE_LABEL}</FieldLabel>
              <FieldDropdown
                action={(_, value) => value && setStoryLanguage(value)}
                propertyName="storyLanguage"
                defaultValue={storyLanguage}
                values={ORIGINAL_VIDEO_LANGUAGE_OPTIONS}
              />
            </Field>
          )}
          {showFolderPicker && (
            <Field>
              <FieldLabel as="span">{FOLDER_LABEL}</FieldLabel>
              <FieldDropdown
                action={(_, value) => setSelectedFolderId?.(value)}
                propertyName="folder"
                defaultValue={selectedFolderId}
                values={folderOptions}
              />
            </Field>
          )}
        </TwoColumns>
      )}

      <Note>
        <InfoIcon />
        <span>{AFTER_SAVE_NOTE}</span>
      </Note>

      <DialogFooter>
        <PrimaryButton
          type="submit"
          disabled={!canSave}
          data-testid="story-details-save"
        >
          Save
        </PrimaryButton>
      </DialogFooter>
    </Form>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

// Same treatment as the Input primitive's own label, for the fields that are
// not an Input (radio groups, dropdowns).
const FieldLabel = styled.label`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
`;

/**
 * Two short fields side by side. Each column shrinks (`minmax(0, 1fr)`) so a
 * long folder or language name truncates instead of widening the card; a
 * lone field takes the full row.
 */
const TwoColumns = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: var(--spacing-md);
`;

// Matches the Input primitive's height (20px line + 2 × 8px padding + border)
// so the dropdowns line up with the Story Name field above them.
const FieldDropdown = styled(ItemDropdownSelect)`
  .Selected {
    height: var(--leading-text-sm);
  }
`;

const HelperRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-sm);
`;

const Helper = styled.span<{ $error?: boolean }>`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: ${props =>
    props.$error ? 'var(--destructive)' : 'var(--text-muted)'};
`;

/**
 * The line under a choice explains the current answer. It always reserves two
 * lines: answers explain themselves in one or two, and a step whose height
 * jumped with every click would move Save under the pointer.
 */
const ChoiceExplanation = styled(Helper)`
  display: block;
  min-height: calc(2 * var(--leading-text-xs));
`;
